import type { ExportFormat } from "@/modules/export";

/** Um intervalo de indisponibilidade do profissional, dentro do período consultado (RN05/RN06). */
export interface ProfissionalPeriodo {
  /** Data de início da indisponibilidade (ISO, sem hora). */
  inicio: string;
  /** Data de fim da indisponibilidade (ISO, sem hora). */
  fim: string;
}

/**
 * Indisponibilidade do profissional no período consultado (RN05/RN06): a tela
 * exibe apenas a tag "Indisponível" e o intervalo início→fim — o motivo nunca é
 * exibido, mesmo que o serviço o informe.
 */
export interface ProfissionalIndisponibilidade {
  indisponivel: boolean;
  periodos: ProfissionalPeriodo[];
}

/** Profissional do módulo Recursos (HU 1761). Consulta somente leitura, sem cadastro/edição. */
export interface Profissional {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  cargo: string;
  time: string;
  /** URL da foto do profissional; vazia quando não há imagem (exibida só no hover do nome). */
  fotoUrl: string;
  indisponibilidade: ProfissionalIndisponibilidade;
  /** Só vem quando a consulta leva `alocacaoId`: já tem alocação nas datas exatas dela. */
  possuiAlocacaoNoPeriodo?: boolean;
}

/** Opção de um filtro de coluna: o rótulo na tela e o valor enviado na consulta. */
export interface ProfissionalFilterOption {
  value: string;
  label: string;
}

/**
 * Colunas filtráveis do painel de Filtros. Cargo e Time têm cada uma o seu
 * serviço `GET .../profissionais/filtros/{coluna}`; Indisponível tem opções
 * estáticas (Sim/Não), sem endpoint.
 */
export const PROFISSIONAIS_FILTER_COLUMNS = ["cargo", "time", "indisponivel"] as const;

export type ProfissionalFilterColumn = (typeof PROFISSIONAIS_FILTER_COLUMNS)[number];

/** Colunas cujas opções vêm de um serviço (todas, exceto Indisponível). */
export type ProfissionalRemoteFilterColumn = Exclude<ProfissionalFilterColumn, "indisponivel">;

export const PROFISSIONAIS_FILTER_LABELS: Record<ProfissionalFilterColumn, string> = {
  cargo: "Cargo",
  time: "Time",
  indisponivel: "Indisponível",
};

/** Valores do filtro Indisponível (seleção única, Sim/Não conforme o protótipo). */
export const INDISPONIVEL_FILTER_YES = "sim";
export const INDISPONIVEL_FILTER_NO = "nao";

export const INDISPONIVEL_FILTER_OPTIONS: ProfissionalFilterOption[] = [
  { value: INDISPONIVEL_FILTER_YES, label: "Sim" },
  { value: INDISPONIVEL_FILTER_NO, label: "Não" },
];

/** Critérios do painel de Filtros: cada coluna aceita vários valores. */
export type ProfissionaisFilters = Record<ProfissionalFilterColumn, string[]>;

export const EMPTY_PROFISSIONAIS_FILTERS: ProfissionaisFilters = {
  cargo: [],
  time: [],
  indisponivel: [],
};

/** Há ao menos um valor escolhido em alguma coluna — decide o empty state de filtros. */
export function hasProfissionaisFilters(filters: ProfissionaisFilters): boolean {
  return PROFISSIONAIS_FILTER_COLUMNS.some((column) => filters[column].length > 0);
}

export interface ProfissionalFilterOptionsState {
  options: ProfissionalFilterOption[];
  isLoading: boolean;
  hasError: boolean;
}

export interface ListProfissionalFilterOptionsParams {
  column: ProfissionalRemoteFilterColumn;
  signal?: AbortSignal;
}

/**
 * Colunas de dado ordenáveis. "Indisponibilidade no Período" é um intervalo
 * derivado do período consultado, sem valor único para comparar, e por isso não
 * entra no cabeçalho ordenável.
 */
export type ProfissionaisSortField = "nome" | "telefone" | "cargo" | "time";

export const PROFISSIONAIS_SORT_FIELDS: ProfissionaisSortField[] = [
  "nome",
  "telefone",
  "cargo",
  "time",
];

export type ProfissionaisSortDirection = "asc" | "desc";

export interface ProfissionaisSort {
  field: ProfissionaisSortField;
  direction: ProfissionaisSortDirection;
}

/** Ordem padrão da tela: pelo nome do profissional, de A a Z. */
export const DEFAULT_PROFISSIONAIS_SORT: ProfissionaisSort = { field: "nome", direction: "asc" };

export function isProfissionaisSortField(value: unknown): value is ProfissionaisSortField {
  return (
    typeof value === "string" && PROFISSIONAIS_SORT_FIELDS.includes(value as ProfissionaisSortField)
  );
}

/**
 * Período Mês/Ano consultado — obrigatório em toda consulta da listagem (RN02).
 * `from`/`to` no formato `AAAA-MM`, o mesmo do contrato (`periodoInicio`/`periodoFim`).
 */
export interface ProfissionaisPeriodo {
  from: string;
  to: string;
}

export interface ListProfissionaisParams {
  /** Texto livre do campo "Buscar nome, time...". */
  busca?: string;
  filters?: Partial<ProfissionaisFilters>;
  /** Período Mês/Ano consultado; obrigatório no contrato. */
  periodo: ProfissionaisPeriodo;
  /** Página 1-based, como no contrato. */
  page?: number;
  pageSize?: number;
  sort?: ProfissionaisSort;
  /** Alocação a remanejar: a listagem passa a marcar `possuiAlocacaoNoPeriodo`. */
  alocacaoId?: string;
  signal?: AbortSignal;
}

export interface ProfissionaisPage {
  profissionais: Profissional[];
  total: number;
}

export type ProfissionaisStatus = "loading" | "success" | "error" | "forbidden";

export interface ProfissionaisQueryState {
  status: ProfissionaisStatus;
  profissionais: Profissional[];
  total: number;
  errorMessage: string | null;
}

/** Critérios da exportação da visualização atual, sem paginação. */
export interface ExportProfissionaisParams {
  busca?: string;
  filters?: Partial<ProfissionaisFilters>;
  /** Rótulos dos filtros da tela, impressos no arquivo. */
  filtros?: string[];
  periodo: ProfissionaisPeriodo;
  sort?: ProfissionaisSort;
  format: ExportFormat;
  signal?: AbortSignal;
}
